import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VideocamIcon from '@mui/icons-material/Videocam';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function socketUrl() {
  const base = API_URL.replace(/\/$/, '').replace(/^http/, 'ws');
  return `${base}/network-cameras/ws`;
}

export default function NetworkCamerasPage() {
  const [session, setSession] = useState(null);
  const [status, setStatus] = useState('connecting');
  const [cameras, setCameras] = useState([]);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [roomVersion, setRoomVersion] = useState(0);
  const [staleIds, setStaleIds] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [liveIds, setLiveIds] = useState({});
  const urlsRef = useRef({});
  const lastFrameRef = useRef({});
  const socketRef = useRef(null);

  const paintFrame = useCallback((id, blob) => {
    const url = URL.createObjectURL(blob);
    const previous = urlsRef.current[id];
    urlsRef.current[id] = url;
    lastFrameRef.current[id] = Date.now();
    document.querySelectorAll(`[data-netcam="${id}"]`).forEach((img) => {
      img.src = url;
    });
    setLiveIds((prev) => (prev[id] ? prev : { ...prev, [id]: true }));
    if (previous) URL.revokeObjectURL(previous);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let socket;

    async function start() {
      setStatus('connecting');
      setError('');
      setCameras([]);
      setLiveIds({});
      setExpandedId(null);
      Object.values(urlsRef.current).forEach((url) => URL.revokeObjectURL(url));
      urlsRef.current = {};
      lastFrameRef.current = {};

      try {
        const res = await fetch(`${API_URL}/api/network-cameras/session`, { method: 'POST' });
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.message || 'Could not start a camera room');
        }
        if (cancelled) return;
        setSession(data);

        socket = new WebSocket(socketUrl());
        socketRef.current = socket;
        socket.binaryType = 'arraybuffer';
        socket.onopen = () => {
          socket.send(JSON.stringify({ type: 'join', role: 'viewer', roomId: data.roomId }));
        };
        socket.onmessage = (event) => {
          if (typeof event.data === 'string') {
            let msg;
            try {
              msg = JSON.parse(event.data);
            } catch (_) {
              return;
            }
            if (msg.type === 'joined') {
              setStatus('live');
              setCameras(msg.cameras || []);
            } else if (msg.type === 'camera-joined') {
              setCameras((prev) => (
                prev.some((cam) => cam.id === msg.camera.id) ? prev : [...prev, msg.camera]
              ));
            } else if (msg.type === 'camera-left') {
              setCameras((prev) => prev.filter((cam) => cam.id !== msg.id));
              setExpandedId((current) => (current === msg.id ? null : current));
              const url = urlsRef.current[msg.id];
              if (url) URL.revokeObjectURL(url);
              delete urlsRef.current[msg.id];
              delete lastFrameRef.current[msg.id];
              setLiveIds((prev) => {
                if (!prev[msg.id]) return prev;
                const next = { ...prev };
                delete next[msg.id];
                return next;
              });
            } else if (msg.type === 'camera-renamed') {
              setCameras((prev) => prev.map((cam) => (
                cam.id === msg.camera.id ? { ...cam, name: msg.camera.name } : cam
              )));
            } else if (msg.type === 'error') {
              setError(msg.message);
              setStatus('error');
            }
            return;
          }

          const bytes = new Uint8Array(event.data);
          const idLength = bytes[0];
          if (!idLength || 1 + idLength >= bytes.length) return;
          const id = new TextDecoder().decode(bytes.subarray(1, 1 + idLength));
          const jpeg = new Blob([event.data.slice(1 + idLength)], { type: 'image/jpeg' });
          paintFrame(id, jpeg);
        };
        socket.onerror = () => {
          if (!cancelled) {
            setError('Could not connect to the camera server. Start the backend and refresh this page.');
          }
        };
        socket.onclose = () => {
          if (!cancelled) setStatus((current) => (current === 'error' ? current : 'disconnected'));
        };
      } catch (err) {
        if (!cancelled) {
          setStatus('error');
          setError(err.message || 'Could not start the camera room');
        }
      }
    }

    start();
    return () => {
      cancelled = true;
      if (socket) socket.close();
      if (socketRef.current === socket) socketRef.current = null;
    };
  }, [roomVersion, paintFrame]);

  useEffect(() => () => {
    Object.values(urlsRef.current).forEach((url) => URL.revokeObjectURL(url));
  }, []);

  useEffect(() => {
    if (!expandedId) return undefined;
    const url = urlsRef.current[expandedId];
    if (!url) return undefined;
    document.querySelectorAll(`[data-netcam="${expandedId}"]`).forEach((img) => {
      if (img.src !== url) img.src = url;
    });
    return undefined;
  }, [expandedId]);

  useEffect(() => {
    const timer = setInterval(() => {
      const now = Date.now();
      setStaleIds((prev) => {
        const ids = Object.keys(lastFrameRef.current);
        const next = {};
        let changed = Object.keys(prev).length !== ids.length;
        ids.forEach((id) => {
          next[id] = now - lastFrameRef.current[id] > 2500;
          if (next[id] !== prev[id]) changed = true;
        });
        return changed ? next : prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const copyLink = async () => {
    if (!session?.joinUrl) return;
    try {
      await navigator.clipboard.writeText(session.joinUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (_) {
      setCopied(false);
    }
  };

  const removeCamera = (id) => {
    const socket = socketRef.current;
    if (socket && socket.readyState === 1) {
      socket.send(JSON.stringify({ type: 'kick', id }));
    }
  };

  const expanded = cameras.find((cam) => cam.id === expandedId) || null;
  const statusLabel = status === 'live' ? 'Watching' : status === 'connecting' ? 'Connecting' : status === 'disconnected' ? 'Disconnected' : 'Unavailable';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: { xs: 2, md: 4 } }}>
      <Container maxWidth="lg">
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>Network cameras</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              Scan the QR with a phone, or point a Raspberry Pi camera at it. Each one shows up here as a live feed.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              color={status === 'live' ? 'success' : status === 'connecting' ? 'warning' : 'default'}
              label={statusLabel}
            />
            <Button startIcon={<RefreshIcon />} variant="outlined" onClick={() => setRoomVersion((value) => value + 1)}>
              New QR
            </Button>
          </Stack>
        </Stack>

        {error && (
          <Paper sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'error.light' }}>
            <Typography color="error.main">{error}</Typography>
          </Paper>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, gap: 2.5, alignItems: 'start' }}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>Add a camera</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Phones and the Pi must be on the same Wi‑Fi. A phone opens the link and taps start. A Pi camera running the camera script joins by itself as soon as it sees this QR.
            </Typography>
            <Box sx={{
              bgcolor: '#fff',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              p: 1.5,
              display: 'grid',
              placeItems: 'center',
              minHeight: 280,
            }}>
              {session?.qr ? (
                <img src={session.qr} alt="QR code that opens the camera link" width="260" height="260" />
              ) : (
                <Typography color="text.secondary">Preparing QR code…</Typography>
              )}
            </Box>
            {session?.joinUrl && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
                <Typography variant="caption" sx={{ flex: 1, wordBreak: 'break-all', color: 'text.secondary' }}>
                  {session.joinUrl}
                </Typography>
                <Tooltip title={copied ? 'Copied' : 'Copy link'}>
                  <IconButton size="small" onClick={copyLink} aria-label="Copy camera link">
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {session
                ? (session.secure
                  ? 'If the phone says the connection is not private, choose Advanced and continue. This computer created that certificate so the browser will allow the camera.'
                  : 'The secure camera link did not start. Phone browsers block camera access on a plain network address.')
                : 'Creating a secure link for phones on this network.'}
            </Typography>
            {session?.host && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Network address {session.host}
              </Typography>
            )}
          </Paper>

          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="h6">Live feeds</Typography>
              <Chip icon={<VideocamIcon />} label={`${cameras.length} connected`} variant="outlined" />
            </Stack>
            {cameras.length === 0 ? (
              <Paper sx={{ p: 6, textAlign: 'center' }}>
                <VideocamIcon sx={{ fontSize: 42, color: 'text.disabled', mb: 1 }} />
                <Typography variant="h6">No cameras yet</Typography>
                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                  Scan the QR code, allow the camera, and the live view appears here.
                </Typography>
              </Paper>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                {cameras.map((cam) => (
                  <Paper key={cam.id} sx={{ overflow: 'hidden' }}>
                    <Box
                      onClick={() => setExpandedId(cam.id)}
                      sx={{ position: 'relative', bgcolor: '#020617', aspectRatio: '4 / 3', cursor: 'pointer' }}
                    >
                      <img
                        data-netcam={cam.id}
                        alt=""
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                      <Chip
                        size="small"
                        label={!liveIds[cam.id] ? 'Starting' : staleIds[cam.id] ? 'Reconnecting' : 'Live'}
                        color={!liveIds[cam.id] || staleIds[cam.id] ? 'warning' : 'success'}
                        sx={{ position: 'absolute', top: 10, left: 10 }}
                      />
                    </Box>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 1 }}>
                      <Box>
                        <Typography sx={{ fontWeight: 650 }}>{cam.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{cam.id}</Typography>
                      </Box>
                      <Tooltip title="Remove camera">
                        <IconButton aria-label={`Remove ${cam.name}`} onClick={() => removeCamera(cam.id)}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Container>

      <Dialog open={Boolean(expanded)} onClose={() => setExpandedId(null)} maxWidth="md" fullWidth>
        {expanded && (
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
              <Typography variant="h6">{expanded.name}</Typography>
              <IconButton onClick={() => setExpandedId(null)} aria-label="Close large view">
                <CloseIcon />
              </IconButton>
            </Stack>
            <Box sx={{ bgcolor: '#020617' }}>
              <img
                data-netcam={expanded.id}
                alt=""
                style={{ width: '100%', maxHeight: '75vh', objectFit: 'contain', display: 'block' }}
              />
            </Box>
          </Box>
        )}
      </Dialog>
    </Box>
  );
}
